import { yupResolver } from '@hookform/resolvers/yup';
import { marketingOptIn } from 'api/marketingOptIn';
import Button from 'components/button';
import Input from 'components/form/input';
import { useConfig } from 'config';
import useMobile from 'hooks/common/use-mobile';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { EventName, trackEvent } from 'utils/tracking';
import emailValidationSchema from 'validations/email';
import * as yup from 'yup';
import { ISectionCustomerIoLeadFormFields } from '../../../../global';

interface FormType {
  newsletter_email: string;
  petType?: 'dog' | 'cat';
}

const CustomerIoLeadForm: React.FC<ISectionCustomerIoLeadFormFields> = ({
  title,
  subTitle,
  leadEventName,
  buttonText,
  formType,
  hideLegalText = false,
}) => {
  const { commonLinks, frontendUrl } = useConfig();

  const intl = useIntl();
  const [sent, setSent] = React.useState(false);
  const { asPath } = useRouter();
  const currentUrl = useMemo(
    () => `${frontendUrl}${asPath.split('#')[0]}`,
    [asPath, frontendUrl]
  );

  const isMobile = useMobile();

  const validationSchema = yup.object({
    newsletter_email: emailValidationSchema,
  });

  const { register, handleSubmit, formState } = useForm<FormType>({
    defaultValues: {
      newsletter_email: '',
      petType: undefined,
    },
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
  });

  const onSubscribeDogsNewsletter = handleSubmit(async (data) => {
    const { newsletter_email } = data;
    trackEvent(EventName.NewsletterSignup);

    await marketingOptIn({
      email: newsletter_email,
      petType: 'dog',
      source: leadEventName,
      opt_in_url: currentUrl,
    });
    setSent(true);
  });

  const onSubscribeCatsNewsletter = handleSubmit(async (data) => {
    const { newsletter_email } = data;
    trackEvent(EventName.NewsletterSignup);
    await marketingOptIn({
      email: newsletter_email,
      petType: 'cat',
      source: leadEventName,
      opt_in_url: currentUrl,
    });

    setSent(true);
  });

  return (
    <div className="bg-grey-100 pt-8 pb-8">
      <div className="container">
        <h2
          className="mindset-24 lg:mindset-40 text-primary-main text-center"
          style={{ fontSize: isMobile ? '24px' : '40px' }}
        >
          {title}
        </h2>

        <div className="mx-auto max-w-xl">
          <p className="text-center font-medium">{subTitle}</p>
          <div className="mt-4 flex flex-col items-center lg:flex-row">
            {sent ? (
              <div className="mx-auto text-center text-base font-medium md:text-lg">
                <FormattedMessage
                  id="footer:newsletter-thank-you"
                  values={{ br: <br /> }}
                />
              </div>
            ) : (
              <div className="w-full">
                <Input
                  aria-label="E-Mail"
                  className="bg-other-white text-text-light-bg-secondary h-10 w-full rounded-sm border px-3 leading-none focus:outline-hidden"
                  type="email"
                  name="newsletter_email"
                  register={register}
                  placeholder={intl.formatMessage({
                    id: 'footer:newsletter-input-placeholder',
                  })}
                  formState={formState}
                />

                <div className="-mx-2 flex">
                  {(formType === 'Both' || formType === 'Dog') && (
                    <div className="w-full px-2">
                      <Button
                        className="w-full"
                        layout="orange"
                        disabled={formState.isSubmitting}
                        onClick={onSubscribeDogsNewsletter}
                      >
                        <span
                          className="inline-block pr-1 text-lg"
                          style={{ transform: 'translateY(1px)' }}
                          role="img"
                          aria-label="Für Hunde"
                        >
                          🐶
                        </span>{' '}
                        {buttonText}
                      </Button>
                    </div>
                  )}

                  {(formType === 'Both' || formType === 'Cat') && (
                    <div className="w-full px-2">
                      <Button
                        className="w-full"
                        layout="orange"
                        disabled={formState.isSubmitting}
                        onClick={onSubscribeCatsNewsletter}
                      >
                        <span
                          className="inline-block pr-1 text-lg"
                          style={{ transform: 'translateY(1px)' }}
                          role="img"
                          aria-label="Für Katzen"
                        >
                          🐱
                        </span>{' '}
                        {buttonText}
                      </Button>
                    </div>
                  )}
                </div>

                {!hideLegalText && (
                  <div
                    className="pt-4 leading-tight"
                    style={{ fontSize: '11px' }}
                  >
                    <FormattedMessage
                      id="footer:newsletter-unsubscribe-notice"
                      values={{
                        a: (chunks) => (
                          <a
                            href={`/pages/${commonLinks.dataProtection}`}
                            className="underline"
                          >
                            {chunks}
                          </a>
                        ),
                        br: () => <br></br>,
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerIoLeadForm;
